/**
 * Basis der API.
 *
 * Im Web ein relativer Pfad — dort liegt die API unter derselben Domain.
 * In der Capacitor-App gibt es diese Domain nicht: der Webview lädt aus dem
 * lokalen Dateisystem, ein relatives "/api" ginge ins Leere. Deshalb kommt
 * die Adresse dort aus der Build-Konfiguration (.env.capacitor).
 */
const BASE = import.meta.env.VITE_API_BASE || '/api';

// Ein absoluter Wert bedeutet: die Anfragen gehen über Domaingrenzen. Dann
// braucht es CORS mit Anmeldedaten, sonst kommt die Sitzung nicht mit.
const FREMD = /^https?:\/\//i.test(BASE);

async function request(path, { method = 'GET', body, admin = false, formData } = {}) {
  const res = await fetch(`${BASE}${path}`, {
    method,
    // Bei FormData KEIN Content-Type setzen — der Browser muss die
    // multipart-Grenze selbst anhängen, sonst kommt nichts an.
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: formData ?? (body ? JSON.stringify(body) : undefined),
    // Bei fremder Domain muss die Sitzung ausdrücklich mitgeschickt werden,
    // sonst ist man in der App bei jedem Aufruf abgemeldet.
    credentials: (admin || FREMD) ? 'include' : 'same-origin',
  });

  let data = null;
  try {
    data = await res.json();
  } catch {
    // Antwort ohne Body — z.B. bei 204.
  }

  if (!res.ok) {
    const err = new Error(data?.error || `Anfrage fehlgeschlagen (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return data;
}

export const api = {
  events: {
    list: (scope = 'current') => request(`/events.php?scope=${encodeURIComponent(scope)}`),
    get: (slug) => request(`/events.php?slug=${encodeURIComponent(slug)}`),
  },
  pokemon: {
    list: () => request('/pokemon.php'),
    get: (slug) => request(`/pokemon.php?slug=${encodeURIComponent(slug)}`),
  },
  raids: {
    list: (scope = 'all') => request(`/raids.php?scope=${encodeURIComponent(scope)}`),
  },
  admin: {
    session: () => request('/admin/session.php', { admin: true }),
    login: (username, password) =>
      request('/admin/session.php', { method: 'POST', body: { username, password }, admin: true }),
    logout: () => request('/admin/session.php', { method: 'DELETE', admin: true }),
    listEvents: () => request('/admin/events.php', { admin: true }),
    createEvent: (payload) =>
      request('/admin/events.php', { method: 'POST', body: payload, admin: true }),
    updateEvent: (id, payload) =>
      request(`/admin/events.php?id=${id}`, { method: 'PUT', body: payload, admin: true }),
    deleteEvent: (id) =>
      request(`/admin/events.php?id=${id}`, { method: 'DELETE', admin: true }),
  },

  auth: {
    me: () => request('/auth.php', { admin: true }),
    login: (email, password) =>
      request('/auth.php?aktion=login', { method: 'POST', body: { email, password }, admin: true }),
    register: (email, password, name) =>
      request('/auth.php?aktion=register', { method: 'POST', body: { email, password, name }, admin: true }),
    logout: () => request('/auth.php', { method: 'DELETE', admin: true }),
    verify: (token) =>
      request('/auth.php?aktion=verify', { method: 'POST', body: { token }, admin: true }),
    requestReset: (email) =>
      request('/auth.php?aktion=reset', { method: 'POST', body: { email }, admin: true }),
    setPassword: (token, password) =>
      request('/auth.php?aktion=reset-set', { method: 'POST', body: { token, password }, admin: true }),
  },

  // Auswerten aendert nichts — erst scanSpeichern legt an oder ersetzt.
  scan: (datei) => {
    const daten = new FormData();
    daten.append('bild', datei);
    return request('/scan.php', { method: 'POST', formData: daten, admin: true });
  },

  scanToken: {
    ausstellen: (geraet) =>
      request('/scan-token.php', { method: 'POST', body: { geraet }, admin: true }),
    entwerten: (token) =>
      request('/scan-token.php', { method: 'DELETE', body: { token }, admin: true }),
  },

  scanSpeichern: (angaben) =>
    request('/scan-speichern.php', { method: 'POST', body: angaben, admin: true }),

  vorraete: () => request('/vorraete.php', { admin: true }),

  stats: {
    verlauf: () => request('/stats.php', { admin: true }),
    erfassen: (datei) => {
      const daten = new FormData();
      daten.append('bild', datei);
      return request('/stats.php', { method: 'POST', formData: daten, admin: true });
    },
  },

  raidTeam: (boss) =>
    request('/raid-team.php' + (boss ? '?boss=' + encodeURIComponent(boss) : ''), { admin: true }),

  exemplarAttacken: {
    holen: (id) => request('/exemplar-attacken.php?id=' + id, { admin: true }),
    speichern: (id, auswahl) =>
      request('/exemplar-attacken.php?id=' + id, { method: 'PUT', body: auswahl, admin: true }),
  },

  fangen: () => request('/fangen.php'),

  pvp: () => request('/pvp.php'),

  myPvp: () => request('/my-pvp.php', { admin: true }),

  // Der Plan: was heute und demnaechst lohnt. Oeffentlich abrufbar.
  plan: (tage = 7) => request('/plan.php?tage=' + tage),

  ziele: {
    lesen: () => request('/ziele.php', { admin: true }),
    setzen: (werte) => request('/ziele.php', { method: 'PUT', body: werte, admin: true }),
  },

  myEvents: () => request('/my-events.php', { admin: true }),

  // Event-Teilnahme: nur die Zeitspanne wird gespeichert, die Bilanz rechnet
  // der Server aus den vorhandenen Scans.
  teilnahme: {
    uebersicht: (eventSlug) =>
      request('/event-teilnahme.php' + (eventSlug ? '?event=' + encodeURIComponent(eventSlug) : ''),
        { admin: true }),
    starten: (angaben) =>
      request('/event-teilnahme.php', { method: 'POST', body: angaben, admin: true }),
    beenden: () =>
      request('/event-teilnahme.php?beenden=1', { method: 'POST', body: {}, admin: true }),
    aendern: (id, daten) =>
      request('/event-teilnahme.php?id=' + id, { method: 'PUT', body: daten, admin: true }),
    loeschen: (id) =>
      request('/event-teilnahme.php?id=' + id, { method: 'DELETE', admin: true }),
  },


  collection: {
    status: () => request('/collection.php', { admin: true }),
    entries: (q = '') =>
      request(`/collection.php?list=1&q=${encodeURIComponent(q)}`, { admin: true }),
    update: (id, daten) =>
      request(`/collection.php?id=${id}`, { method: 'PUT', body: daten, admin: true }),
    // Letzte Datei erneut einlesen, ohne sie nochmal auszuwaehlen.
    repeat: () => request('/collection.php?wiederholen=1', { method: 'POST', admin: true }),

    import: (datei) => {
      const fd = new FormData();
      fd.append('datei', datei);
      return request('/collection.php', { method: 'POST', formData: fd, admin: true });
    },
    clear: () => request('/collection.php', { method: 'DELETE', admin: true }),

    // Ein einzelnes Exemplar entfernen — für verschickte Pokémon.
    remove: (id) =>
      request('/collection.php?id=' + id, { method: 'DELETE', admin: true }),
  },
};
